import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Input, Segmented, Select, message } from 'antd';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  GitCompare,
  Search,
  Users,
} from 'lucide-react';
import { PageHeader } from '@/shared/components';
import { Button } from '@/shared/components/Button';
import { KpiCard } from '@/features/budgets/components/KpiCard';
import { useBudgetReportYearsListQuery } from '@/features/budgets/hooks/useBudgetQueries';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import {
  useGroupComparisonsOutlierThresholdResolved,
} from '@/features/settings/hooks/useResolvedDashboardSetting';
import {
  DashboardSettingSourceNotes,
} from '@/features/settings/components/DashboardSettingSourceNote';
import { useGovernoratesQuery } from '@/features/region/hooks/useRegionQueries';
import { useSchoolBandsConfiguredForYear } from '@/features/reports/hooks/useSchoolBandsConfiguredForYear';
import { ClassificationWorkflowRequiredAlert } from '@/features/reports/components/ClassificationWorkflowRequiredAlert';
import { useGroupComparisonsQuery } from '@/features/reports/hooks/useGroupComparisonsQuery';
import { useGroupComparisonsSchoolBandsQuery } from '@/features/reports/hooks/useGroupComparisonsSchoolBandsQuery';
import { useGroupComparisonsTuitionBandsQuery } from '@/features/reports/hooks/useGroupComparisonsTuitionBandsQuery';
import { useGroupComparisonsParentBandsQuery } from '@/features/reports/hooks/useGroupComparisonsParentBandsQuery';
import type {
  GroupComparisonsFilterParams,
  GroupComparisonsMetricFocus,
  GroupComparisonsSchoolRow,
} from '@/features/reports/api/groupComparisons.dto';
import {
  METRIC_FOCUS_OPTIONS,
  focusMetricValue,
  focusPeerMedian,
  focusVariance,
  hasAtLeastOneGroupFilter,
  isOutlierOnFocus,
  metricColumnHeader,
  peerMedianKpiLabel,
} from '@/features/reports/data/groupComparisonsMock';
import {
  exportBasename,
  exportGroupComparisonsExcel,
  exportGroupComparisonsPdf,
} from '@/features/reports/utils/groupComparisonsExports';
import { schoolProfileHref } from '@/app/router/routes';
import { bandFullName } from '@/features/reports/utils/bandDisplayLabels';

type SortKey = 'schoolName' | 'studentsCount' | 'varianceFocus';
type SortDir = 'asc' | 'desc';
type RowViewMode = 'all' | 'outliers';

const ANY_VALUE = '';

function withAnyOption(items: { value: string; label: string }[]) {
  return [{ value: ANY_VALUE, label: 'Any' }, ...items];
}

type BandLookups = {
  governorateName?: string;
  schoolBandName?: string;
  tuitionBandName?: string;
  parentBandName?: string;
  schoolBandDisplay?: string;
  tuitionBandDisplay?: string;
  parentBandDisplay?: string;
};

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtPct(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function abbrevSchoolName(name: string): string {
  return name.length > 16 ? `${name.slice(0, 14)}…` : name;
}

function focusMetricLabel(focus: GroupComparisonsMetricFocus): string {
  return METRIC_FOCUS_OPTIONS.find((o) => o.value === focus)?.label ?? focus;
}

function CohortDistributionChart({
  rows,
  focus,
  peerMedian,
  loading,
  outlierThreshold,
}: {
  rows: GroupComparisonsSchoolRow[];
  focus: GroupComparisonsMetricFocus;
  peerMedian: number;
  loading: boolean;
  outlierThreshold: number;
}) {
  const chartData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => focusMetricValue(b, focus) - focusMetricValue(a, focus))
        .map((r) => ({
          schoolId: r.schoolId,
          name: abbrevSchoolName(r.schoolName),
          value: focusMetricValue(r, focus),
          isOutlier: isOutlierOnFocus(r, focus, outlierThreshold),
        })),
    [rows, focus, outlierThreshold]
  );

  if (loading) {
    return (
      <div
        className="h-[300px] animate-pulse rounded-xl border border-slate-200 bg-slate-100"
        aria-hidden
      />
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-500">
        No data.
      </div>
    );
  }

  return (
    <div className="h-[min(420px,50vh)] w-full min-w-0 sm:h-[360px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 12, right: 16, left: 4, bottom: 56 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            interval={0}
            angle={-32}
            textAnchor="end"
            height={72}
          />
          <YAxis
            tickFormatter={(v) => {
              const n = Number(v);
              if (n >= 1000) return `$${Math.round(n / 100) / 10}k`;
              return `$${Math.round(n)}`;
            }}
            tick={{ fontSize: 11 }}
            width={48}
          />
          <RechartsTooltip
            formatter={(value: number) => [fmtUsd(value), metricColumnHeader(focus)]}
            labelFormatter={(label) => String(label)}
          />
          <ReferenceLine
            y={peerMedian}
            stroke="#64748b"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            label={{
              value: 'Median',
              position: 'insideTopRight',
              fill: '#64748b',
              fontSize: 11,
            }}
          />
          <Bar dataKey="value" name={metricColumnHeader(focus)} radius={[4, 4, 0, 0]} maxBarSize={40}>
            {chartData.map((entry) => (
              <Cell key={entry.schoolId} fill={entry.isOutlier ? '#d97706' : '#4f46e5'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 rounded-xl border border-slate-200/90 bg-white p-4" aria-hidden>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-slate-100" />
      ))}
    </div>
  );
}

function buildFilterParams(
  year: string,
  governorateId: string,
  schoolBandId: string,
  tuitionBandId: string,
  parentBandId: string,
  metricFocus: GroupComparisonsMetricFocus,
  lookups: BandLookups
): GroupComparisonsFilterParams {
  const params: GroupComparisonsFilterParams = {
    year: year.trim(),
    metricFocus,
  };
  if (governorateId.trim()) {
    params.governorateId = Number(governorateId);
    if (lookups.governorateName) params.governorateName = lookups.governorateName;
  }
  if (schoolBandId.trim()) {
    params.schoolBandId = schoolBandId.trim();
    if (lookups.schoolBandName) params.schoolBandName = lookups.schoolBandName;
    if (lookups.schoolBandDisplay) params.schoolBandDisplay = lookups.schoolBandDisplay;
  }
  if (tuitionBandId.trim()) {
    params.tuitionBandId = tuitionBandId.trim();
    if (lookups.tuitionBandName) params.tuitionBandName = lookups.tuitionBandName;
    if (lookups.tuitionBandDisplay) params.tuitionBandDisplay = lookups.tuitionBandDisplay;
  }
  if (parentBandId.trim()) {
    params.parentBandId = parentBandId.trim();
    if (lookups.parentBandName) params.parentBandName = lookups.parentBandName;
    if (lookups.parentBandDisplay) params.parentBandDisplay = lookups.parentBandDisplay;
  }
  return params;
}

export function GroupComparisonsPage() {
  const reportYearsQuery = useBudgetReportYearsListQuery();
  const defaultYear = useDefaultYear();
  const outlierThresholdResolved = useGroupComparisonsOutlierThresholdResolved();
  const outlierThreshold = outlierThresholdResolved.value;

  const [year, setYear] = useState('');
  const [governorateId, setGovernorateId] = useState(ANY_VALUE);
  const [schoolBandId, setSchoolBandId] = useState(ANY_VALUE);
  const [tuitionBandId, setTuitionBandId] = useState(ANY_VALUE);
  const [parentBandId, setParentBandId] = useState(ANY_VALUE);
  const [metricFocus, setMetricFocus] = useState<GroupComparisonsMetricFocus>('cost');
  const [tableSearch, setTableSearch] = useState('');
  const [rowView, setRowView] = useState<RowViewMode>('all');
  const [applied, setApplied] = useState<GroupComparisonsFilterParams | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('varianceFocus');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    if (!year && defaultYear) setYear(defaultYear);
  }, [defaultYear, year]);

  useEffect(() => {
    setSchoolBandId(ANY_VALUE);
    setTuitionBandId(ANY_VALUE);
    setParentBandId(ANY_VALUE);
    setApplied(null);
  }, [year]);

  const governoratesQuery = useGovernoratesQuery();
  const schoolBandsQuery = useGroupComparisonsSchoolBandsQuery(year);
  const tuitionBandsQuery = useGroupComparisonsTuitionBandsQuery(year);
  const parentBandsQuery = useGroupComparisonsParentBandsQuery(year);

  const governorateOptions = useMemo(() => {
    const items = (governoratesQuery.data ?? []).map((g) => ({
      value: String(g.id),
      label: g.name,
    }));
    return withAnyOption(items);
  }, [governoratesQuery.data]);

  const schoolBandOptions = useMemo(() => {
    const items = (schoolBandsQuery.data?.items ?? []).map((b) => ({
      value: b.id,
      label: bandFullName(b, 'students'),
    }));
    return withAnyOption(items.filter((o) => o.value.trim()));
  }, [schoolBandsQuery.data?.items]);

  const tuitionBandOptions = useMemo(() => {
    let items = tuitionBandsQuery.data?.items ?? [];
    if (schoolBandId.trim()) {
      items = items.filter((tb) => tb.schoolBandId === schoolBandId.trim());
    }
    return withAnyOption(
      items.map((tb) => ({
        value: tb.id,
        label: bandFullName(tb, 'usd'),
      }))
    );
  }, [tuitionBandsQuery.data?.items, schoolBandId]);

  const parentBandOptions = useMemo(() => {
    const items = (parentBandsQuery.data?.items ?? []).map((b) => ({
      value: b.id ?? '',
      label: bandFullName(b, 'usd'),
    }));
    return withAnyOption(items.filter((o) => o.value.trim()));
  }, [parentBandsQuery.data?.items]);

  const resolveLookups = useCallback(
    (govId: string, sbId: string, tbId: string, pbId: string): BandLookups => {
      const sb = schoolBandsQuery.data?.items.find((b) => b.id === sbId.trim());
      const tb = tuitionBandsQuery.data?.items.find((t) => t.id === tbId.trim());
      const pb = parentBandsQuery.data?.items.find((p) => p.id === pbId.trim());
      return {
        governorateName: governoratesQuery.data?.find((g) => String(g.id) === govId)?.name,
        schoolBandName: sb?.name?.trim() || undefined,
        tuitionBandName: tb?.name?.trim() || undefined,
        parentBandName: pb?.name?.trim() || undefined,
        schoolBandDisplay: sb ? bandFullName(sb, 'students') : undefined,
        tuitionBandDisplay: tb ? bandFullName(tb, 'usd') : undefined,
        parentBandDisplay: pb ? bandFullName(pb, 'usd') : undefined,
      };
    },
    [governoratesQuery.data, schoolBandsQuery.data?.items, tuitionBandsQuery.data?.items, parentBandsQuery.data?.items]
  );

  const queryYear = useMemo(() => {
    const y = year.trim();
    if (!/^\d+$/.test(y)) return null;
    return parseInt(y, 10);
  }, [year]);

  const bandsGate = useSchoolBandsConfiguredForYear(year);
  /** Report loads from API when year is valid; classification alert is advisory. */
  const canRunReport = queryYear != null;

  const searchQuery = useGroupComparisonsQuery(applied, canRunReport && applied != null);

  const yearOptions = useMemo(() => {
    const raw = reportYearsQuery.data ?? [];
    return [...raw].sort((a, b) => b - a).map((n) => ({ value: String(n), label: String(n) }));
  }, [reportYearsQuery.data]);

  const focus = applied?.metricFocus ?? metricFocus;
  const cohortRows = searchQuery.data?.rows ?? [];

  const viewFilteredRows = useMemo(() => {
    let rows = cohortRows;
    if (rowView === 'outliers') {
      rows = rows.filter((r) => isOutlierOnFocus(r, focus, outlierThreshold));
    }
    const q = tableSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.schoolName.toLowerCase().includes(q) ||
        r.schoolCode.toLowerCase().includes(q) ||
        r.governorateName.toLowerCase().includes(q)
    );
  }, [cohortRows, rowView, focus, tableSearch, outlierThreshold]);

  const sortedRows = useMemo(() => {
    return [...viewFilteredRows].sort((a, b) => {
      const m = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'schoolName') {
        return a.schoolName.localeCompare(b.schoolName, undefined, { sensitivity: 'base' }) * m;
      }
      if (sortKey === 'varianceFocus') {
        const va = Math.abs(focusVariance(a, focus) ?? 0);
        const vb = Math.abs(focusVariance(b, focus) ?? 0);
        return (va - vb) * m;
      }
      return (a.studentsCount - b.studentsCount) * m;
    });
  }, [viewFilteredRows, sortKey, sortDir, focus]);

  const handleSearch = useCallback(() => {
    if (!year.trim()) {
      message.warning('Select a year.');
      return;
    }
    const lookups = resolveLookups(governorateId, schoolBandId, tuitionBandId, parentBandId);
    const params = buildFilterParams(
      year,
      governorateId,
      schoolBandId,
      tuitionBandId,
      parentBandId,
      metricFocus,
      lookups
    );
    if (!hasAtLeastOneGroupFilter(params)) {
      message.warning('Select at least one filter: governorate and/or a classification band.');
      return;
    }
    setApplied(params);
    setTableSearch('');
    setRowView('all');
    setSortKey('varianceFocus');
    setSortDir('desc');
  }, [
    year,
    governorateId,
    schoolBandId,
    tuitionBandId,
    parentBandId,
    metricFocus,
    resolveLookups,
  ]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'schoolName' ? 'asc' : 'desc');
    }
  };

  const summary = searchQuery.data?.summary;
  const distribution = searchQuery.data?.distribution;
  const composedLabel = searchQuery.data?.composedPeerGroupLabel;
  const isLoading = Boolean(canRunReport && applied && searchQuery.isFetching);

  const handleExportExcel = async () => {
    if (sortedRows.length === 0) {
      message.warning('No rows to export.');
      return;
    }
    try {
      await exportGroupComparisonsExcel(sortedRows, focus, exportBasename(year, focus), outlierThreshold);
      message.success('Downloaded.');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Export failed.');
    }
  };

  const handleExportPdf = async () => {
    if (sortedRows.length === 0) {
      message.warning('No rows to export.');
      return;
    }
    try {
      await exportGroupComparisonsPdf(sortedRows, focus, exportBasename(year, focus), [
        `Group Comparisons — ${year}`,
        composedLabel ?? '',
        rowView === 'outliers' ? 'Outliers' : 'All',
      ], outlierThreshold);
      message.success('Downloaded.');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Export failed.');
    }
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const emptyTableMessage = useMemo(() => {
    if (cohortRows.length === 0) return 'No schools match these filters.';
    if (tableSearch.trim()) return 'No schools match this search.';
    if (rowView === 'outliers') return 'No outliers. Try All.';
    return 'No schools to show.';
  }, [cohortRows.length, tableSearch, rowView]);

  return (
    <div className="space-y-6 pb-10">
      <PageHeader title="Group Comparisons" />

      <div className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[8rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Year</label>
            <Select
              className="w-full min-w-[8rem]"
              placeholder="Year"
              value={year || undefined}
              onChange={setYear}
              options={yearOptions}
              loading={reportYearsQuery.isPending}
            />
          </div>
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Governorate</label>
            <Select
              className="w-full min-w-[10rem]"
              value={governorateId || ANY_VALUE}
              onChange={setGovernorateId}
              options={governorateOptions}
              loading={governoratesQuery.isPending}
            />
          </div>
          <div className="min-w-[12rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">School size band</label>
            <Select
              className="w-full"
              value={schoolBandId || ANY_VALUE}
              onChange={(v) => {
                setSchoolBandId(v);
                setTuitionBandId(ANY_VALUE);
              }}
              options={schoolBandOptions}
              loading={schoolBandsQuery.isPending}
            />
          </div>
          <div className="min-w-[11rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Tuition band</label>
            <Select
              className="w-full"
              value={tuitionBandId || ANY_VALUE}
              onChange={setTuitionBandId}
              options={tuitionBandOptions}
              loading={tuitionBandsQuery.isPending}
            />
          </div>
          <div className="min-w-[11rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Parent band</label>
            <Select
              className="w-full"
              value={parentBandId || ANY_VALUE}
              onChange={setParentBandId}
              options={parentBandOptions}
              loading={parentBandsQuery.isPending}
            />
          </div>
          <div className="min-w-[11rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Metric focus</label>
            <Select
              className="w-full"
              value={metricFocus}
              onChange={(v) => setMetricFocus(v)}
              options={METRIC_FOCUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </div>
          <Button type="button" variant="primary" className="px-5 py-2 text-sm" onClick={handleSearch}>
            Search
          </Button>
        </div>
      </div>

      {queryYear != null && bandsGate.isPending ? (
        <p className="text-center text-sm text-slate-600">Loading…</p>
      ) : null}

      {queryYear != null && bandsGate.notConfigured ? (
        <ClassificationWorkflowRequiredAlert yearLabel={year || String(queryYear)} />
      ) : null}

      {canRunReport && applied && searchQuery.isError ? (
        <Alert type="error" showIcon message="Could not load data." />
      ) : null}

      {canRunReport && applied && !searchQuery.isError ? (
        <>
          {composedLabel && !isLoading ? (
            <>
              <div className="rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Peer group</p>
                <p className="mt-1 text-lg font-semibold text-indigo-950">{composedLabel}</p>
                <p className="mt-2 text-sm text-slate-700">
                  <span className="font-semibold tabular-nums">{summary?.schoolCount ?? 0}</span> schools · Median{' '}
                  <span className="font-semibold tabular-nums">
                    {distribution ? fmtUsd(distribution.peerMedian) : '—'}
                  </span>
                </p>
              </div>

              {summary != null && summary.schoolCount < 3 ? (
                <Alert type="warning" showIcon message="Fewer than 3 schools — comparison may be unreliable." />
              ) : null}
            </>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <KpiCard label="Schools" value={summary?.schoolCount ?? 0} icon={Users} accent="indigo" />
            <KpiCard
              label="Outliers"
              value={summary ? `${summary.outlierCount} of ${summary.schoolCount}` : '—'}
              sublabel={`≥${outlierThresholdResolved.displayValue} vs peers`}
              icon={AlertTriangle}
              accent="rose"
            />
            <KpiCard
              label={peerMedianKpiLabel(focus)}
              value={summary ? fmtUsd(focusPeerMedian(summary, focus)) : '—'}
              icon={GitCompare}
              accent="emerald"
            />
          </div>

          <DashboardSettingSourceNotes
            resolved={[outlierThresholdResolved]}
            className="px-1"
          />

          <section
            aria-labelledby="cohort-chart-heading"
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-100"
          >
            <h2 id="cohort-chart-heading" className="text-base font-semibold text-slate-900">
              Distribution — {focusMetricLabel(focus)}
            </h2>
            <div className="mt-4">
              <CohortDistributionChart
                rows={cohortRows}
                focus={focus}
                peerMedian={distribution?.peerMedian ?? 0}
                loading={isLoading}
                outlierThreshold={outlierThreshold}
              />
            </div>
          </section>

          <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-slate-200/80 bg-slate-50/60 px-4 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <Segmented
                value={rowView}
                onChange={(v) => setRowView(v as RowViewMode)}
                options={[
                  { label: 'Outliers', value: 'outliers' },
                  { label: 'All', value: 'all' },
                ]}
              />
              <div>
                <label htmlFor="group-comparisons-table-search" className="mb-1 block text-xs font-semibold text-slate-700">
                  Search schools
                </label>
                <Input
                  id="group-comparisons-table-search"
                  allowClear
                  prefix={<Search className="size-4 text-slate-400" aria-hidden />}
                  placeholder="Name or code"
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  className="w-full min-w-[14rem] sm:w-56"
                  aria-label="Search schools by name or code"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pb-0.5">
              <Button
                type="button"
                variant="secondary"
                className="inline-flex items-center gap-1.5 text-xs"
                onClick={handleExportExcel}
                disabled={sortedRows.length === 0 || isLoading}
              >
                <FileSpreadsheet className="size-3.5" aria-hidden />
                Excel
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="inline-flex items-center gap-1.5 text-xs"
                onClick={handleExportPdf}
                disabled={sortedRows.length === 0 || isLoading}
              >
                <FileText className="size-3.5" aria-hidden />
                PDF
              </Button>
            </div>
          </div>

          {isLoading ? (
            <TableSkeleton />
          ) : sortedRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-12 text-center text-sm text-slate-600">
              {emptyTableMessage}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100">
              <table className="w-full min-w-[800px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <th className="px-3 py-3">
                      <button type="button" className="font-semibold text-slate-700" onClick={() => toggleSort('schoolName')}>
                        School{sortIndicator('schoolName')}
                      </button>
                    </th>
                    <th className="px-3 py-3 font-semibold text-slate-700">Governorate</th>
                    <th className="px-3 py-3 font-semibold text-slate-700">Code</th>
                    <th className="px-3 py-3 text-right">
                      <button type="button" className="font-semibold text-slate-700" onClick={() => toggleSort('studentsCount')}>
                        Students{sortIndicator('studentsCount')}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-right font-semibold text-slate-700">{metricColumnHeader(focus)}</th>
                    <th className="px-3 py-3 text-right">
                      <button type="button" className="font-semibold text-slate-700" onClick={() => toggleSort('varianceFocus')}>
                        vs peers{sortIndicator('varianceFocus')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const fv = focusVariance(row, focus);
                    const highlightVar =
                      fv != null && Math.abs(fv) >= outlierThreshold;
                    const outlierOnFocus = isOutlierOnFocus(row, focus, outlierThreshold);
                    const focusReason =
                      row.outlierReasons.find((r) => {
                        if (focus === 'cost') return r.startsWith('Cost');
                        if (focus === 'tuition') return r.startsWith('Tuition');
                        return r.startsWith('Salary');
                      }) ?? null;

                    return (
                      <tr key={row.schoolId} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="max-w-[14rem] px-3 py-2">
                          <a
                            href={schoolProfileHref({
                              schoolId: row.schoolId,
                              year: applied?.year ?? year,
                              governorateId: row.governorateId,
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-indigo-700 hover:underline"
                          >
                            {row.schoolName}
                          </a>
                          {outlierOnFocus ? (
                            <span
                              className="ml-1.5 inline-flex align-middle text-amber-700"
                              title={focusReason ?? 'Outlier'}
                              aria-label={focusReason ? `Outlier: ${focusReason}` : 'Outlier'}
                            >
                              <AlertTriangle className="size-3.5" aria-hidden />
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{row.governorateName}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{row.schoolCode}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.studentsCount.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-900">
                          {fmtUsd(focusMetricValue(row, focus))}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono text-xs tabular-nums ${
                            highlightVar ? 'font-bold text-amber-900' : 'text-slate-700'
                          }`}
                        >
                          {fmtPct(fv)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      {!applied ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-12 text-center text-sm text-slate-600">
          Select a year and at least one filter (governorate or band), then click <strong>Search</strong>.
        </div>
      ) : null}
    </div>
  );
}
